import { DOCUMENT } from '@angular/common';
import { inject, Injectable, InjectionToken, NgZone } from '@angular/core';
import { environment } from '@environments/environments';
import { Observable, timeout } from 'rxjs';

/** reCAPTCHA v3 site key. Defaults to the environment key; override only for tests. */
export const RECAPTCHA_V3_SITE_KEY = new InjectionToken<string>('recaptcha-v3-site-key', {
    providedIn: 'root',
    factory: () => environment.captcha_key,
});

interface Grecaptcha {
    ready(callback: () => void): void;
    execute(siteKey: string, options: { action: string }): Promise<string>;
}

interface WindowWithRecaptcha {
    grecaptcha?: Grecaptcha;
}

const SCRIPT_URL = 'https://www.google.com/recaptcha/api.js';

/** Longest we wait for reCAPTCHA (script load + token) before failing, so forms never stay locked. */
const EXECUTE_TIMEOUT_MS = 15000;

function whenReady(grecaptcha: Grecaptcha): Promise<Grecaptcha> {
    return new Promise((resolve) => grecaptcha.ready(() => resolve(grecaptcha)));
}

/** Loads the reCAPTCHA script once per page (root scoped, so concurrent consumers share one load). */
@Injectable({ providedIn: 'root' })
export class RecaptchaLoader {
    private readonly document = inject(DOCUMENT);
    private readonly siteKey = inject(RECAPTCHA_V3_SITE_KEY);

    /** Pending script load, shared so concurrent consumers do not inject the script twice. */
    private scriptLoading: Promise<void> | null = null;

    /** Resolves the reCAPTCHA API, injecting its script first when it is not on the page yet. */
    async load(): Promise<Grecaptcha> {
        const win = this.document.defaultView as (Window & WindowWithRecaptcha) | null;
        if (!win?.grecaptcha?.execute) {
            await this.injectScript();
        }
        const grecaptcha = win?.grecaptcha;
        if (!grecaptcha) throw new Error('reCAPTCHA is not available');
        return whenReady(grecaptcha);
    }

    private injectScript(): Promise<void> {
        if (this.scriptLoading !== null) return this.scriptLoading;

        const loading = new Promise<void>((resolve, reject) => {
            const url = new URL(SCRIPT_URL);
            url.searchParams.set('render', this.siteKey);
            url.searchParams.set('trustedtypes', 'true');

            const script = this.document.createElement('script');
            script.src = url.href;
            script.async = true;
            script.defer = true;
            // A stalled request fires neither load nor error; give up so a later call can inject the script again.
            const deadline = setTimeout(() => {
                script.remove();
                reject(new Error('Timed out loading the reCAPTCHA script'));
            }, EXECUTE_TIMEOUT_MS);
            script.onload = () => {
                clearTimeout(deadline);
                resolve();
            };
            script.onerror = () => {
                clearTimeout(deadline);
                reject(new Error('Failed to load the reCAPTCHA script'));
            };
            this.document.head.appendChild(script);
        });
        this.scriptLoading = loading;

        // Allow a later call to retry if the script could not be loaded (e.g. blocked or offline).
        loading.catch(() => (this.scriptLoading = null));
        return loading;
    }
}

/** Executes reCAPTCHA v3 actions. Drop-in replacement for the `ng-recaptcha` service of the same name. */
@Injectable({ providedIn: 'root' })
export class ReCaptchaV3Service {
    private readonly zone = inject(NgZone);
    private readonly loader = inject(RecaptchaLoader);
    private readonly siteKey = inject(RECAPTCHA_V3_SITE_KEY);

    constructor() {
        // Start loading the script right away so the badge shows and the first execute() is fast.
        this.loader.load().catch(() => undefined);
    }

    /**
     * Executes the given reCAPTCHA action and emits the token once, then completes.
     * Errors if the script fails to load, reCAPTCHA rejects the execution, or it takes longer than 15s.
     */
    execute(action: string): Observable<string> {
        return new Observable<string>((subscriber) => {
            this.loader.load()
                .then((grecaptcha) => this.zone.runOutsideAngular(() => grecaptcha.execute(this.siteKey, { action })))
                .then(
                    (token) => this.zone.run(() => {
                        subscriber.next(token);
                        subscriber.complete();
                    }),
                    (error: unknown) => this.zone.run(() => subscriber.error(error)),
                );
        }).pipe(timeout(EXECUTE_TIMEOUT_MS));
    }
}
